import { use, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import DetailsPopUpContent from 'shared/components/details-pop-up-content/details-pop-up-content';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import Permission from 'shared/features/auth/authorization/enums/permission';

import PermissionsPermission from 'features/role/domain/models/permissions-permission';
import Role from 'features/role/domain/models/role';
import { RoleType } from 'features/role/domain/models/role-type';
import { useGetPermissions, useUpdatePermissions } from 'features/role/domain/queries/permission-queries';
import { useCreateRole, useUpdateRole } from 'features/role/domain/queries/role-queries';
import RoleDetailsPopupHeader from './role-details-popup-header';
import RoleDetailsPopupRightContent from './role-details-popup-right-content';
import RoleInformation from './role-information.component';

import './role-details-popup.component.scss';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (role: Role) => void;
  onDelete: () => void;
  selectedRole?: Role;
  allRoles: Array<Role> | undefined;
  defaultViewingMode: ViewingMode;
}

const RoleDetailsPopup = (props: Readonly<IProps>): React.JSX.Element => {
  const { showDefaultConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { mutateAsync: createRole, isSuccess: createRoleIsSuccess, error: createRoleError, data: createRoleData } = useCreateRole();
  const { mutateAsync: updateRole, isSuccess: updateRoleIsSuccess, error: updateRoleError, data: updateRoleData } = useUpdateRole();
  const { mutateAsync: updatePermissions, isSuccess: updatePermissionsIsSuccess, error: updatePermissionsError } = useUpdatePermissions();

  const [viewingMode, setViewingMode] = useState(props.defaultViewingMode);
  const [hasInitiatedEditingMode, setHasInitiatedEditingMode] = useState(false);

  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    error: getPermissionsError,
    isFetching: isFetchingPermissions,
    refetch: refetchPermissions,
  } = useGetPermissions(props.selectedRole?.idpId ?? '', false);

  const isLoading = isLoadingPermissions || isFetchingPermissions;

  const [isSaving, setIsSaving] = useState(false);

  const mapDefaultPermissions = (): Record<Permission, boolean> => {
    const map = new Map();
    for (const value of Object.values(Permission)) {
      map.set(value, false);
    }
    return Object.fromEntries(map) as Record<Permission, boolean>;
  };

  const defaultValues = {
    permissions: mapDefaultPermissions(),
    name: props.selectedRole?.name ?? '',
    idpId: props.selectedRole?.idpId ?? '',
    description: props.selectedRole?.description ?? '',
    roleType: props.selectedRole?.roleType ?? RoleType.Permissions,
    accessOrganisationUnitId: props.selectedRole?.accessOrganisationUnitId ?? '',
    canBeAssignedToTemporaryUsers: props.selectedRole?.canBeAssignedToTemporaryUsers ?? false,
  };

  const form = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const watchRoleType = useWatch({ control: form.control, name: 'roleType' });

  useEffect(() => {
    if (props.defaultViewingMode !== 'creation' && props.selectedRole?.roleType === RoleType.Permissions) {
      refetchPermissions();
    }
  }, [props.defaultViewingMode]);

  useEffect(() => {
    if (getPermissionsError) {
      setErrorMessage(getPermissionsError);
    }
  }, [getPermissionsError]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
    return () => enablePopUpGuard(false);
  }, [form.formState.isDirty]);

  const mapPermissionsToRecord = (values?: Array<PermissionsPermission>): Record<Permission, boolean> => {
    const map = new Map();
    for (const value of Object.values(Permission)) {
      map.set(value, values?.find((permissionsPermission) => permissionsPermission.permission === value)?.isPermissionEnabled ?? false);
    }
    return Object.fromEntries(map) as Record<Permission, boolean>;
  };

  useEffect(() => {
    const newPermissions = permissions ? mapPermissionsToRecord(permissions) : mapDefaultPermissions();
    const newValues = { ...defaultValues, permissions: newPermissions };
    form.reset(newValues);
  }, [permissions]);

  const submitForm = async (fieldValues: FieldValues): Promise<void> => {
    if (fieldValues.roleType === RoleType.Access && !form.getValues('accessOrganisationUnitId')) {
      return;
    }

    setIsSaving(true);
    await submitRole(fieldValues);

    if (fieldValues.roleType === RoleType.Permissions) {
      await submitPermissions(fieldValues);
    }
  };

  useEffect(() => {
    if (createRoleIsSuccess || updateRoleIsSuccess) {
      const data = viewingMode === 'creation' ? createRoleData : updateRoleData;
      if (data) {
        form.reset({ ...form.getValues() });
        props.onSaved(data);
      }

      setViewingMode('viewing');
      setIsSaving(false);
    }
  }, [createRoleIsSuccess, updateRoleIsSuccess, createRoleData, updateRoleData]);

  useEffect(() => {
    if (!updatePermissionsIsSuccess) {
      setErrorMessage(updatePermissionsError ?? undefined);
    }
  }, [updatePermissionsIsSuccess, updatePermissionsError]);

  useEffect(() => {
    if ((!createRoleIsSuccess || !updatePermissionsIsSuccess) && (createRoleError || updateRoleError)) {
      setErrorMessage(viewingMode === 'creation' ? (createRoleError ?? undefined) : (updateRoleError ?? undefined));

      setIsSaving(false);
    }
  }, [createRoleError, createRoleIsSuccess, updateRoleError, updateRoleIsSuccess]);

  const submitRole = async (fieldValues: FieldValues): Promise<void> => {
    const accessOrganisationUnitId = form.getValues('accessOrganisationUnitId');
    const role: Role = {
      id: props.selectedRole?.id,
      name: fieldValues.name?.trim(),
      idpId: fieldValues.idpId.trim(),
      description: fieldValues.description?.trim(),
      roleType: fieldValues.roleType,
      accessOrganisationUnitId: accessOrganisationUnitId !== '' ? accessOrganisationUnitId : undefined,
      canBeAssignedToTemporaryUsers: fieldValues.canBeAssignedToTemporaryUsers,
    };

    if (viewingMode === 'creation') {
      await createRole(role);
    } else {
      await updateRole({
        originalIdpId: props.selectedRole?.idpId ?? '',
        role,
      });
    }
  };

  const submitPermissions = async (fieldValues: FieldValues): Promise<void> => {
    const permissions = Object.entries(fieldValues.permissions as Record<Permission, boolean>).map(([permission, isPermissionEnabled]) => ({
      permission: permission as Permission,
      isPermissionEnabled,
    }));
    await updatePermissions({
      idpId: fieldValues.idpId.trim(),
      permissions,
    });
  };

  const handleCancel = (closePopup: boolean): void => {
    form.reset();
    if (closePopup) {
      props.onClose();
    } else {
      setViewingMode('viewing');
    }
  };

  const handleOnClose = (closePopup: boolean): void => {
    showDefaultConfirmationPopUp({
      shouldShow: (viewingMode === 'creation' || viewingMode === 'editing') && form.formState.isDirty,
      action: () => {
        handleCancel(closePopup);
      },
    });
  };

  const handleOnEdit = (): void => {
    setHasInitiatedEditingMode(true);
    setViewingMode('editing');
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleOnClose(true)}
      className="role-details-popup"
      slotProps={{
        paper: {
          className: 'role-details-popup-paper',
        },
      }}
    >
      <FormProvider {...form}>
        <DetailsPopUpContent
          onSubmit={form.handleSubmit(submitForm)}
          isDisabled={isLoading || isSaving}
          leftContent={<RoleInformation selectedRole={props.selectedRole} allRoles={props.allRoles} viewingMode={viewingMode} />}
          rightContent={
            isLoading ? (
              <LoadingIndicator />
            ) : (
              <RoleDetailsPopupRightContent
                roleType={watchRoleType}
                viewingMode={viewingMode}
                initialSelectedOrganisationUnit={props.selectedRole?.accessOrganisationUnitId}
              />
            )
          }
          header={
            <RoleDetailsPopupHeader
              onClose={() => handleOnClose(true)}
              onCancel={() => handleCancel(!hasInitiatedEditingMode)}
              onEdit={handleOnEdit}
              onDelete={props.onDelete}
              viewingMode={viewingMode}
              isLoading={isLoading || isSaving}
            />
          }
        />
      </FormProvider>
    </Dialog>
  );
};

export default RoleDetailsPopup;
